import { z } from 'zod';

/**
 * Gate scanner input. The QR token is either:
 *   - `mem:{membership_id}:{hmac_hex}`  (member rotating QR)
 *   - `gst:{token}` or bare `{token}`   (guest static single-use QR)
 */
export const RedeemCheckInSchema = z.object({
  qr_token: z
    .string()
    .min(8, 'QR token is too short')
    .max(2048, 'QR token is too long'),
  scan_method: z
    .enum(['qr', 'nfc', 'manual', 'hardware'])
    .default('qr'),
  gate_label: z.string().max(64).nullable().optional(),
});

export type RedeemCheckInInput = z.infer<typeof RedeemCheckInSchema>;

/**
 * Server response shape returned by the redeem_check_in RPC. Always includes
 * `ok`; on success the response carries subject details. On failure it carries
 * a stable `reason` code and optional context.
 */
export interface RedeemCheckInResponse {
  ok: boolean;
  idempotent?: boolean;
  reason?:
    | 'invalid_qr'
    | 'unpaid'
    | 'expired'
    | 'wrong_date'
    | 'already_used'
    | 'revoked'
    | 'already_checked_in_today'
    | 'membership_not_active'
    | 'membership_expired'
    | 'unpaid_dues'
    | 'outside_access_hours';
  detail?: string | null;
  scanned_at?: string;
  valid_for_date?: string;
  status?: string;
  payment_status?: string;
  subject_type?: 'member' | 'guest';
  member_name?: string;
  member_number?: string | null;
  plan_name?: string;
  notes?: string | null;
  /**
   * Member-only: number of guest passes (pending_payment + active + used)
   * the scanned member is bringing today. Surfaced on the reception banner
   * per PRD §10.2. Undefined on guest scans.
   */
  guests_with_member_today?: number;
  guest_name?: string;
  age_bucket?: 'adult' | 'kid';
  inviter_name?: string | null;
  inviter_plan?: string | null;
  inviter_label?: string | null;
  /** Set on outside_access_hours denials: the tier's window as 'HH:MM:SS'. */
  access_start?: string | null;
  access_end?: string | null;
}
